import {css} from '@mui/material';
import {useFormatters} from '@wallaby-ui/document';
import {Column} from 'material-table';
import * as React from 'react';
import {useMemo} from 'react';

import {UzisSimpleTable, ValueCellWithCopy} from './UzisReportTableCommon';
import {UzisReportVIResult} from './uzisreporttypes';

type Props = {
  data: UzisReportVIResult | undefined;
  isLoading: boolean | undefined;
  storageIdPrefix: string;
};

type TableRow = {
  totalLabel: string;
  rowNumber: number | string;
  col1: number;
  col2: number;
  col3: number;
  col4: number;
};

export const UzisReportTableVI: React.FC<Props> = ({data, isLoading = true, storageIdPrefix}) => {
  const {formatFloat, t} = useFormatters();

  const tableData: TableRow[] = useMemo(() => {
    if (!data) return [];
    return [
      {
        totalLabel: 'a',
        rowNumber: 'b',
        col1: 1,
        col2: 2,
        col3: 3,
        col4: 4,
      },
      {
        totalLabel: t('total'),
        rowNumber: 61,
        col1: data.total,
        col2: data.drugStarted,
        col3: data.drugStopped,
        col4: data.dosageChanged,
      },
    ];
  }, [data, t]);

  const columns: Column<TableRow>[] = useMemo(
    () => [
      {
        field: 'totalLabel',
        title: t('reports.uzis.tableVI.totalInterventions'),
        render: (rowData) => <span style={{fontWeight: 500}}>{rowData.totalLabel}</span>,
        sorting: false,
        width: '150px',
        cellStyle: {minWidth: '150px', maxWidth: '150px', width: '150px', textAlign: 'center'},
        headerStyle: {minWidth: '150px', maxWidth: '150px', width: '150px', textAlign: 'center'},
      },
      {
        field: 'rowNumber' as any,
        title: t('reports.uzis.common.rowNumber'),
        render: (rowData) => <span style={{fontWeight: 500}}>{rowData.rowNumber}</span>,
        sorting: false,
        cellStyle: {width: '60px', textAlign: 'center', color: '#9e9e9e'},
        headerStyle: {width: '60px', textAlign: 'center'},
      },
      {
        field: 'col1',
        title: t('reports.uzis.tableVI.allInterventions'),
        render: (rowData) => (
          <ValueCellWithCopy
            value={rowData.col1}
            formatInteger={formatFloat}
            showCopyButton={rowData?.tableData?.id != null && rowData?.tableData?.id > 0}
          />
        ),
        sorting: false,
        cellStyle: {textAlign: 'center', minWidth: '150px', width: '150px'},
        headerStyle: {textAlign: 'center', minWidth: '150px', width: '150px'},
      },
      {
        field: 'col2',
        title: t('hospitalization.detail.phRec.combination.shortcuts.system.drugStarted'),
        render: (rowData) => (
          <ValueCellWithCopy
            value={rowData.col2}
            formatInteger={formatFloat}
            showCopyButton={rowData?.tableData?.id != null && rowData?.tableData?.id > 0}
          />
        ),
        sorting: false,
        cellStyle: {textAlign: 'center', minWidth: '120px'},
        headerStyle: {textAlign: 'center', minWidth: '120px'},
      },
      {
        field: 'col3',
        title: t('hospitalization.detail.phRec.combination.shortcuts.system.drugStopped'),
        render: (rowData) => (
          <ValueCellWithCopy
            value={rowData.col3}
            formatInteger={formatFloat}
            showCopyButton={rowData?.tableData?.id != null && rowData?.tableData?.id > 0}
          />
        ),
        sorting: false,
        cellStyle: {textAlign: 'center', minWidth: '120px'},
        headerStyle: {textAlign: 'center', minWidth: '120px'},
      },
      {
        field: 'col4',
        title: t('hospitalization.detail.phRec.combination.shortcuts.system.drugChanged'),
        render: (rowData) => (
          <ValueCellWithCopy
            value={rowData.col4}
            formatInteger={formatFloat}
            showCopyButton={rowData?.tableData?.id != null && rowData?.tableData?.id > 0}
          />
        ),
        sorting: false,
        cellStyle: {textAlign: 'center', minWidth: '150px'},
        headerStyle: {textAlign: 'center', minWidth: '150px'},
      },
      {
        field: 'empty2' as any,
        title: '',
        render: () => <span />,
        sorting: false,
        cellStyle: {width: '60px', textAlign: 'center'},
        headerStyle: {width: '60px', textAlign: 'center'},
      },
    ],
    [formatFloat, t]
  );

  return (
    <UzisSimpleTable<TableRow>
      storageScrollKey={`${storageIdPrefix}.tableVI.scroll`}
      headerLabel='reports.uzis.tableVI.title'
      columns={columns}
      data={tableData}
      isLoading={isLoading}
      options={{
        rowStyle: (_rowData, index) => ({
          color: index === 0 ? '#9e9e9e' : undefined,
        }),
      }}
      tableAutoWidthStyle={css`
        & table thead tr th[col-id='totalLabel'] {
          width: 150px;
          max-width: 150px;
        }

        & table thead tr th[col-id='col1'] {
          width: 120px;
          max-width: 120px;
        }
      `}
    />
  );
};
